import { memo, useCallback } from "react";
import { TableCell } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { iRawTrainingData } from "./trainingDataConverter";

interface TextAreaTrainingDataProps {
    row: iRawTrainingData;
    editingId: number | null;
    editValues: { message1: string; message2: string; message3: string };
    setEditValues: React.Dispatch<React.SetStateAction<{ message1: string; message2: string; message3: string }>>;
    handleStartEdit: (item: iRawTrainingData) => void;
    messageId: number;
}

function TextAreaTrainingData({ row, editingId, editValues, setEditValues, handleStartEdit, messageId }: TextAreaTrainingDataProps) {
    const messageKey = `message${messageId + 1}` as keyof typeof editValues;
    const isEditing = editingId === row.rowId;
    const rowId = row.rowId;
    
    const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        if (editingId !== rowId) {
            handleStartEdit(row);
        }
        setEditValues(prev => ({ ...prev, [messageKey]: value }));
    }, [editingId, rowId, row, handleStartEdit, setEditValues, messageKey]);

    const handleFocus = useCallback(() => {
        if (editingId !== rowId) {
            handleStartEdit(row);
        }
    }, [editingId, rowId, row, handleStartEdit]);

    return (
        <TableCell>
            <Textarea
                value={isEditing ? editValues[messageKey] : row.messages[messageId]?.content || ''}
                onChange={handleChange}
                onFocus={handleFocus}
                className="min-h-[120px] resize-y"
            />
        </TableCell>
    );
}

export default memo(TextAreaTrainingData, (prevProps, nextProps) => {
    // Only re-render if the specific props we care about have changed
    if (prevProps.editingId !== nextProps.editingId) return false;
    if (prevProps.messageId !== nextProps.messageId) return false;
    if (prevProps.row.rowId !== nextProps.row.rowId) return false;
    
    // If this row is being edited, check if the edit values changed
    if (nextProps.editingId === nextProps.row.rowId) {
        const messageKey = `message${nextProps.messageId + 1}` as keyof typeof nextProps.editValues;
        if (prevProps.editValues[messageKey] !== nextProps.editValues[messageKey]) return false;
    } else {
        // If not editing, check if the row's message content changed
        if (prevProps.row.messages[prevProps.messageId]?.content !== nextProps.row.messages[nextProps.messageId]?.content) return false;
    }
    
    return true; // Props are equal, skip re-render
});